import { propOr, toString, ifElse, is, pipe, prop, has, mergeRight, always, reject, isNil, test, isEmpty, assoc, not, equals } from 'ramda';
import sanitizeUrl from './sanitizeUrl';

const defaultHeaders = {
  'Content-Type': 'application/json',
};

const addSubscriptionKey = (headers) => {
  return assoc('Ocp-Apim-Subscription-Key', process.env.REACT_APP_OCP_APIM_SUBSCRIPTION_KEY_CALCULO_EXCEDENTE, headers);
}

const defaultMethod = 'GET';

const isSuccessResponse = (code) => (code >= 200 && code < 300);

const supportEmptyResponse = async (response) => {
  const clonedResponse = await response.clone();
  const contentText = await clonedResponse.text();
  return isEmpty(contentText) ? contentText : response.json();
};

/**
 * Service Fetcher Factory
 * @param {string} options - options to create a Service Fetcher.
 * @param {string} options - options.baseUrl - Base request url.
 * @param {string} options - options.tokenUrl - Token url.
 * @param {string} options - options.tokenRequestOptions - Token request options.
 * @param {string} options - options.accessTokenGetter - Access token getter.
 * @param {string} options - options.onError - Callback for error.
 * @param {string} options - options.onGetToken - Callback for get token.
 */

export default function serviceFetcherCreate(options) {
  const BASE_URL = propOr('', 'baseUrl', options);
  const TOKEN_URL = propOr('', 'tokenUrl', options);
  const tokenRequestOptions = propOr('', 'tokenRequestOptions', options);
  const accessTokenGetter = propOr('', 'accessTokenGetter', options);
  const onError = propOr(() => { }, 'onError', options);
  const onGetToken = propOr(() => { }, 'onGetToken', options);

  const requestUrl = ifElse(
    test(/(https?:\/\/)/g),
    (value) => value,
    (value) => `${BASE_URL}${value}`,
  );

  const getTokenFetch = async () => {
    const tokenResponse = await serviceFetcher(TOKEN_URL, assoc('isTokenFetch', true, tokenRequestOptions));
    onGetToken(tokenResponse);
  };

  const addAuth = (headers) => {
    return assoc('Authorization', `Bearer ${accessTokenGetter()}`, headers);
  };

  const generateNormalOptions = () => (requestOptions) => reject(isNil, {
    method: propOr(defaultMethod, 'method', requestOptions),
    headers: mergeRight(addSubscriptionKey(addAuth(defaultHeaders)), prop('headers', requestOptions)),
    body: ifElse(has('body'), pipe(prop('body'), toString), always(null))(requestOptions),
  });

  const generateFormDataOptions = () => (requestOptions) => reject(isNil, {
    method: propOr(defaultMethod, 'method', requestOptions),
    headers: mergeRight(addSubscriptionKey(addAuth({})), prop('headers', requestOptions)),
    body: prop('body', requestOptions),
  });

  const parseError = (errorResponse, res) => {
    const error = new Error();
    error.message = ifElse(has('errorMessage'), prop('errorMessage'), prop('message'))(errorResponse);
    error.status = res.status;
    error.url = res.url;
    error.data = errorResponse;
    return error;
  };

  const responseTypeParser = async (response, optionType) => {
    switch (optionType) {
      case 'blob':
        return response.blob();
      case 'text':
        return response.text();
      default:
        return supportEmptyResponse(response);
    }
  }

  /**
   * Service Fetcher
   * @param {string} url - Request url.
   * @param {object} requestOptions - Request options.
   * @param {string} requestOptions.method - Request method.
   * @param {object} requestOptions.body - Request body.
   * @param {boolean} requestOptions.featuredResponse - To get extra response.
   * @param {string} requestOptions.responseType - Response type.
   * @param {boolean} requestOptions.avoidGetToken - To avoid the get token process.
   */
  const serviceFetcher = async (url, requestOptions) => {
    const featuredResponse = propOr(false, 'featuredResponse', requestOptions);
    const responseType = propOr('json', 'responseType', requestOptions);
    const isTokenFetch = propOr(false, 'isTokenFetch', requestOptions);

    const _options = ifElse(
      pipe(prop('body'), is(FormData)),
      generateFormDataOptions(),
      generateNormalOptions(),
    )(requestOptions);

    const res = await fetch(requestUrl(sanitizeUrl(url)), _options);
    const responseClone = res.clone();

    if (not(isSuccessResponse(res.status))) {

      if (isTokenFetch) return onError(`Error getting token - Status: ${res.status}`);;

      if (equals(res.status, 401)) {
        try {
          await getTokenFetch();
          // retry original request
          return serviceFetcher(url, requestOptions);
        } catch (error) {
          return onError(`Error getting token - Status: ${res.status}`);
        }
      } else {
        const errorResponse = await res.json();
        const error = parseError(errorResponse, res);
        onError(`Error fetching data - Message: ${error.message} - Status: ${error.status} - Url: ${error.url}`);
        throw error; // send to TryCatchGeneric or error if use swr
      }
    }
    const parsedResponse = await responseTypeParser(responseClone, responseType);
    if (featuredResponse) {
      return {
        status: res.status,
        data: parsedResponse
      };
    }
    return parsedResponse;
  };
  return serviceFetcher;
};

